import React, { useEffect, useState } from 'react';
import { Input, Card, Row, Col, Space, Pagination, Select, Spin, List, Typography, Image, Button, Alert } from 'antd';
import { ObservationMap, PredictionsMap } from '../map/SpeciesMap';
import LanguageSelector from '../elements/LanguageSelector';
import { CSVDownloadButton } from '../elements/SpeciesCSV';
import useLocalStorageObserver from '../hooks/LocalStorageObserver';
import { SpeciesV2 } from '../../interfaces';
import {
  SwapOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomOutOutlined,
  ZoomInOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { HabitatsDirectiveModal, HabitatsDirectiveSINPModal, InvasiveModal, FranceRedListModal, ProtectedListModal, EuropeanRedListModal, IUCNRedListModal, ZnieffModal, ProtectedSINPModal } from '../case/caseFr';
import Cookies from 'js-cookie';
import { useKeycloak } from '@react-keycloak/web';

const { Option } = Select;
const { Text, Link } = Typography;

const SpeciesObservations: React.FC = () => {
  const { keycloak } = useKeycloak(); // Get the keycloak instance

  const [selectedApi, setSelectedApi] = useState<'minka' | 'plantnet_plots' | 'plantnet_occurrences' | 'gbif' | 'sinp'>(
    'minka',
  );
  const [language, setLanguage] = useState<string>(localStorage.getItem('language') ?? 'en'); // manage language state here
  const [observations, setObservations] = useState<any[]>([]); // To store observations data
  const userCaseStudy = keycloak.idTokenParsed?.case_study as string | undefined;

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage); // Update language state
    localStorage.setItem('language', newLanguage); // Save to localStorage
  };
  useEffect(() => {
    setObservations([]);
  }, [selectedApi]);
  const handleApiChange = (value: 'minka' | 'plantnet_plots' | 'plantnet_occurrences' | 'gbif' | 'sinp') => {
    setSelectedApi(value);
  };
  const handleDataRetrieved = (data: any[]) => {
    setObservations(data);
  };

  return (
    <div>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>Home &gt; Species &gt; Observations</h1>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong>Select Data Source:</Text>
                <Select
                  id='speciesSelectSourceId'
                  value={selectedApi}
                  style={{ width: 240 }}
                  onChange={handleApiChange}
                >
                  {/* Conditionally render PlantNet options */}
                  {userCaseStudy !== 'Greece' && (
                    <>
                      <Option value='plantnet_plots'>Pl@ntNet Plots</Option>
                    </>
                  )}
                  {/* Always render these options */}
                  <Option value='plantnet_occurrences'>Pl@ntNet Occurrences</Option>
                  <Option value='minka'>Minka</Option>
                  <Option value='gbif'>GBIF</Option>
                  {/* Conditionally render SINP option for France */}
                  {userCaseStudy === 'France' && <Option value='sinp'>SINP Data</Option>}
                </Select>
              </div>

              {/* Pl@ntNet Alert with link */}
              {(selectedApi === 'plantnet_plots' || selectedApi === 'plantnet_occurrences') && (
                <Alert
                  type='info'
                  showIcon
                  message={
                    <>
                      <Text strong>Data Source:</Text> Pl@ntNet Occurrences and Plots
                      <br />
                      Contributed by citizen scientists worldwide through the Pl@ntNet platform.
                      <br />
                      <Link href='https://plantnet.org' target='_blank'>
                        https://plantnet.org
                      </Link>
                      | Data licensed under CC BY 4.0 | Images under various open licenses
                      <br />
                      <Link href='https://guarden.org/technologies/plantnet/' target='_blank'>
                        Learn more about Pl@ntNet
                      </Link>
                    </>
                  }
                />
              )}

              {/* MINKA Alert with link */}
              {selectedApi === 'minka' && (
                <Alert
                  type='info'
                  showIcon
                  message={
                    <>
                      <Text strong>Data Source:</Text> MINKA Citizen Observatory
                      <br />
                      MINKA API Data Feed. EMBIMOS research group, Institut de Ciències del Mar (ICM-CSIC), Barcelona,
                      Spain.
                      <br />
                      <Link href='https://minka-sdg.org' target='_blank'>
                        https://minka-sdg.org
                      </Link>{' '}
                      | Data licensed under CC BY 4.0
                      <br />
                      <Link href='https://guarden.org/technologies/minka/' target='_blank'>
                        Learn more about MINKA
                      </Link>
                    </>
                  }
                />
              )}

              {/* SINP Alert */}
              {selectedApi === 'sinp' && (
                <Alert
                  type='info'
                  showIcon
                  message={
                    <>
                      <Text strong>Data Source:</Text> Système d'information de l'Inventaire du Patrimoine Naturel
                      (SINP)
                      <br />
                      The SINP allows for the centralisation and structuring of biodiversity observation data produced
                      in France.
                      <br />
                      <Link href='https://inpn.mnhn.fr/informations/sinp/presentation' target='_blank'>
                        https://inpn.mnhn.fr/informations/sinp/presentation
                      </Link>
                    </>
                  }
                />
              )}

              <ObservationMap api={selectedApi} language={language} onDataRetrieved={handleDataRetrieved} />
              <Alert
                type='warning'
                showIcon
                message={
                  <>
                    <Text strong>Note:</Text> The displayed species observations are not comprehensive.
                  </>
                }
              />
            </Space>
          </Card>
        </Col>
        <Col span={24}>
          <SpeciesComponent
            speciesData={observations}
            onLanguageChange={handleLanguageChange}
            type='observations'
            selectedApi={selectedApi}
          />
        </Col>
      </Row>
    </div>
  );
};

const SpeciesPredictions: React.FC = () => {
  const [language, setLanguage] = useState<string>(Cookies.get('language') ?? 'en');
  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };
  const [predictions, setPredictions] = useState<any[]>([]);

  const handleDataRetrieved = (data: any[]) => {
    setPredictions(data);
  };

  return (
    <div>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>Home &gt; Species &gt; Predictions</h1>
      <Row gutter={16}>
        <Col span={24}>
          <Card>
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              {/* Add the new Alert for GeoPl@ntNet */}
              <Alert
                type='info'
                showIcon
                message={
                  <>
                    <Text strong>Data Source:</Text> GeoPl@ntNet
                    <br />
                    <Link href='https://guarden.org/technologies/geoplantnet/' target='_blank'>
                      Learn more about GeoPl@ntNet
                    </Link>
                  </>
                }
              />
              <PredictionsMap language={language} onDataRetrieved={handleDataRetrieved} />
            </Space>
          </Card>
        </Col>
        <Col span={24}>
          <SpeciesComponent
            speciesData={predictions}
            onLanguageChange={handleLanguageChange}
            type={'predictions'}
            selectedApi={undefined}
          />
        </Col>
      </Row>
    </div>
  );
};

const SpeciesComponent: React.FC<{
  speciesData: SpeciesV2[];
  onLanguageChange: (language: string) => void;
  type: string;
  selectedApi?: 'minka' | 'plantnet_plots' | 'plantnet_occurrences' | 'gbif' | 'sinp';
}> = ({ speciesData, onLanguageChange, type, selectedApi }) => {
  const [filteredSpecies, setFilteredSpecies] = useState<SpeciesV2[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterFields, setFilterFields] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [yearFilters, setYearFilters] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isInvasiveModalVisible, setIsInvasiveModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFranceRedListModalVisible, setFranceRedListIsModalVisible] = useState(false);
  const [isProtectedIsModalVisible, setProtectedIsModalVisible] = useState(false);
  const [isIUCNRedListIsModalVisible, setIUCNRedListIsModalVisible] = useState(false);
  const [isZnieffModalVisible, setZnieffModalVisible] = useState(false);
  const [isEuropeanRedListModal, setEuropeanRedListModal] = useState(false);


  const language = useLocalStorageObserver('language') ?? 'en';
  const caseStudy = Cookies.get('caseStudy');
  // Define the fields available for filtering
  const filterableFields = [
    'name',
    'family',
    'commonNames',
    ...(type === 'predictions' ? ['predictedName'] : []),
    'taxaName',
    'category',
  ];

  // Define status/boolean fields
  let statusFields = [
    { key: 'is_endemic', label: 'Endemic' },
    { key: 'is_invasive', label: 'Invasive' },
    { key: 'is_eu_directive', label: 'EU Directive' },
    { key: 'iucn_red_list_category', label: 'IUCN Red List' },
  ];
  if (caseStudy === 'pnr_narbonnaise') {
    statusFields = statusFields.filter(field => field.key !== 'iucn_red_list_category');
  }
  if (caseStudy !== 'cyprus') {
    statusFields = [...statusFields, { key: 'is_grin_uses', label: 'Grin Uses' }];
  }
  if (caseStudy === 'pnr_narbonnaise') {
    statusFields = [...statusFields, { key: 'is_protected', label: 'Protected' }];
    statusFields = [...statusFields, { key: 'group_label', label: 'Group Label' }];
    statusFields = [...statusFields, { key: 'france_red_list', label: 'France Red List' }];
  }
  if (caseStudy === 'barcelona') {
    statusFields = [...statusFields, { key: 'is_protected', label: 'Protected' }];
    statusFields = [...statusFields, { key: 'exotic', label: 'Exotic' }];
    statusFields = [...statusFields, { key: 'threatened', label: 'Threatened' }];
  }

  function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Extract unique years from observed_data
  const availableYears = React.useMemo(() => {
    const years = speciesData.reduce((acc: number[], species) => {
      if (species.observed_data) {
        const date = new Date(species.observed_data);
        const year = date.getFullYear();
        if (!acc.includes(year)) {
          acc.push(year);
        }
      }
      return acc;
    }, []);
    return years.sort((a, b) => a - b);
  }, [speciesData]);

  useEffect(() => {
    const filterData = () => {
      const query = debouncedSearchQuery.toLowerCase();
      let filtered = [...speciesData];

      // Apply text-based filters
      if (query) {
        const fieldsToFilter = filterFields.length > 0 ? filterFields : filterableFields;
        filtered = filtered.filter((species) =>
          fieldsToFilter.some((field) => {
            const value = species[field as keyof SpeciesV2]?.toString().toLowerCase();
            return value && value.includes(query);
          }),
        );
      }

      // Status filters
      if (statusFilters.length > 0) {
        filtered = filtered.filter((species) =>
          statusFilters.every((statusField) => {
            if (statusField === 'iucn_red_list_category') {
              return species[statusField] && species[statusField] !== 'Unknown';
            }
            return species[statusField as keyof SpeciesV2] === 'True';
          }),
        );
      }

      // Year filters
      if (yearFilters.length > 0) {
        filtered = filtered.filter((species) => {
          if (species.observed_data) {
            const speciesYear = new Date(species.observed_data).getFullYear();
            return yearFilters.includes(speciesYear);
          }
          return false;
        });
      }

      setFilteredSpecies(filtered);
    };

    setLoading(true);
    filterData();
    setCurrentPage(1);
    setLoading(false);
  }, [debouncedSearchQuery, filterFields, statusFilters, yearFilters, speciesData]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterFieldsChange = (fields: string[]) => {
    setFilterFields(fields);
    setCurrentPage(1);
  };

  const handleStatusFiltersChange = (filters: string[]) => {
    setStatusFilters(filters);
    setCurrentPage(1);
  };

  const handleYearFiltersChange = (years: number[]) => {
    setYearFilters(years);
    setCurrentPage(1);
  };

  const formatFieldName = (field: string) => {
    const camelCaseFormatted = field.replace(/([a-z])([A-Z])/g, '$1 $2');
    const snakeCaseFormatted = camelCaseFormatted.replace(/_/g, ' ');
    return snakeCaseFormatted
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderSpeciesList = (species: SpeciesV2) => (
    <List.Item key={species.id}>
      <Row gutter={16}>
        <Col span={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            {species.images.slice(0, 3).map((image, index) => (
              <Image
                key={index}
                style={{
                  width: 250,
                  height: 250,
                  objectFit: 'contain',
                  margin: '0 10px', // Horizontal margin between images
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease',
                }}
                src={image.includes('square') ? image.replace('square', 'medium') : image}
                preview={{
                  toolbarRender: (
                    _,
                    {
                      transform: { scale },
                      actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset },
                    },
                  ) => (
                    <Space size={12} className='toolbar-wrapper'>
                      <SwapOutlined rotate={90} onClick={onFlipY} />
                      <SwapOutlined onClick={onFlipX} />
                      <RotateLeftOutlined onClick={onRotateLeft} />
                      <RotateRightOutlined onClick={onRotateRight} />
                      <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} />
                      <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                      <UndoOutlined onClick={onReset} />
                    </Space>
                  ),
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              />
            ))}
          </div>
        </Col>
        <Col span={12}>
          <Card
            title={
              <Row>
                <Col span={12}>
                  <Text strong>{species.name || 'Unknown'}</Text>
                </Col>
              </Row>
            }
          >
            {species.family !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Family: </Text> {species.family || 'Unknown'}
                </Col>
              </Row>
            )}
            {species.commonNames !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Common Names: </Text> {species.commonNames || 'Unknown'}
                </Col>
              </Row>
            )}
            {type === 'predictions' && species.predictedName !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Predicted Name: </Text> {species.predictedName || 'Unknown'}
                </Col>
              </Row>
            )}
            {(caseStudy !== 'cyprus' || type === 'observations') && species.taxaName !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Taxa Name: </Text> {species.taxaName || 'Unknown'}
                </Col>
              </Row>
            )}
            {caseStudy !== 'cyprus' && (
              <>
                {species.taxaId !== undefined && (
                  <Row>
                    <Col span={24}>
                      <Text strong>Taxa ID: </Text> {species.taxaId || 'Unknown'}
                    </Col>
                  </Row>
                )}
                {species.taxaRank !== undefined && (
                  <Row>
                    <Col span={24}>
                      <Text strong>Taxa Rank: </Text> {species.taxaRank || 'Unknown'}
                    </Col>
                  </Row>
                )}
                {species.taxaRankLevel !== undefined && (
                  <Row>
                    <Col span={24}>
                      <Text strong>Taxa Rank Level: </Text> {species.taxaRankLevel || 'Unknown'}
                    </Col>
                  </Row>
                )}
              </>
            )}
            {species.is_endemic !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Endemic: </Text>
                  {species.is_endemic === 'False' ? 'No' : species.is_endemic === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.is_invasive !== undefined && (
              <Row>
                <Col span={24}>
                  {caseStudy === 'pnr_narbonnaise' ? (
                    <Text
                      strong
                      style={{ cursor: 'pointer' }}
                      onClick={() => setIsInvasiveModalVisible(true)}
                    >
                      Invasive:
                    </Text>
                  ) : (
                    <Text strong>Invasive: </Text>
                  )}
                  {species.is_invasive === 'False' ? 'No' : species.is_invasive === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.category !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Category: </Text> {species.category || 'Unknown'}
                </Col>
              </Row>
            )}
            {species.is_eu_directive !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={caseStudy === 'pnr_narbonnaise' ? () => setIsModalVisible(true) : undefined}
                    style={{ padding: 0 }}
                  >
                    <Text strong>EU Directive: </Text>
                  </Button>
                  {species.is_eu_directive === 'False' ? 'No' : species.is_eu_directive === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {caseStudy !== 'cyprus' && species.is_grin_uses !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Grin Uses: </Text>
                  {species.is_grin_uses === 'False' ? 'No' : species.is_grin_uses === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.threatened !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Grin Uses: </Text>
                  {species.threatened === 'False' ? 'No' : species.threatened === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.exotic !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Grin Uses: </Text>
                  {species.exotic === 'False' ? 'No' : species.exotic === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.iucn_red_list_category !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={() => setIUCNRedListIsModalVisible(true)}
                    style={{ padding: 0 }}
                  >
                    <Text strong>IUCN Red List: </Text>
                    {species.iucn_red_list_category || 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
            {caseStudy === 'cyprus' && species.native !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Native: </Text>
                  {species.native === 'False' ? 'No' : species.native === 'True' ? 'Yes' : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.is_protected !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={() => setProtectedIsModalVisible(true)}
                    style={{ padding: 0 }}
                  >
                    <Text strong>Protected: </Text>
                    {species.is_protected === 'False' ? 'No' : species.is_protected === 'True' ? 'Yes' : 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
            {species.observed_data !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Observed On: </Text>
                  {species.observed_data instanceof Date
                    ? species.observed_data.toLocaleString() // Format the Date object
                    : 'Unknown'}
                </Col>
              </Row>
            )}
            {species.european_red_list !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={() => setEuropeanRedListModal(true)}
                    style={{ padding: 0 }}
                  >
                    <Text strong>European Red List: </Text> {species.european_red_list || 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
            {species.france_red_list !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={caseStudy === 'pnr_narbonnaise' ? () => setFranceRedListIsModalVisible(true) : undefined}
                    style={{ padding: 0 }}
                  >
                    <Text strong>France Red List: </Text> {species.france_red_list || 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
            {species.znieff !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={() => setZnieffModalVisible(true)}
                    style={{ padding: 0 }}
                  >
                    <Text strong>ZNIEFF: </Text> {species.znieff || 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
            {species.group_label !== undefined && (
              <Row>
                <Col span={24}>
                  <Text strong>Group Label: </Text> {species.group_label || 'Unknown'}
                </Col>
              </Row>
            )}
            {species.alien_plants !== undefined && (
              <Row>
                <Col span={24}>
                  <Button
                    color='default'
                    variant='link'
                    onClick={() => setIsInvasiveModalVisible(true)}
                    style={{ padding: 0 }}
                  >
                    <Text strong>Alien Plants: </Text> {species.alien_plants || 'Unknown'}
                  </Button>
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>
    </List.Item>
  );

  const paginatedSpecies = filteredSpecies.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleOk = (setVisible: React.Dispatch<React.SetStateAction<boolean>>) => () => {
    setVisible(false);
  };

  const handleCancel = (setVisible: React.Dispatch<React.SetStateAction<boolean>>) => () => {
    setVisible(false);
  };

  return (
    <div>
      <Card title='Species Details' style={{ marginTop: 5 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Input
            id='speciesSelectDataId'
            placeholder='Search Species'
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ width: '30%' }}
          />
          <Select
            id='speciesFilterDataId'
            mode='multiple'
            placeholder='Fields to filter'
            value={filterFields}
            onChange={handleFilterFieldsChange}
            style={{ width: '30%' }}
          >
            {filterableFields.map((field) => (
              <Select.Option key={field} value={field}>
                {formatFieldName(field)}
              </Select.Option>
            ))}
          </Select>
          <Select
            id='speciesFilterDataExtraId'
            mode='multiple'
            placeholder='Filter by status'
            value={statusFilters}
            onChange={handleStatusFiltersChange}
            style={{ width: '30%' }}
          >
            {statusFields
              .filter((field) => !(caseStudy === 'pnr_narbonnaise' && field.key === 'is_endemic'))
              .map((field) => (
                <Select.Option key={field.key} value={field.key}>
                  {field.label}
                </Select.Option>
              ))}
          </Select>
          <Select
            id='speciesFilterYearId'
            mode='multiple'
            placeholder='Filter by Year'
            value={yearFilters}
            onChange={handleYearFiltersChange}
            style={{ width: '20%' }}
          >
            {availableYears.map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
          <LanguageSelector currentLanguage={language} onLanguageChange={onLanguageChange} />
          <CSVDownloadButton data={filteredSpecies} filename='filtered_species.csv' selectedApi={selectedApi} />
        </div>
        {loading ? (
          <Spin size='large' />
        ) : (
          <List itemLayout='vertical' dataSource={paginatedSpecies} renderItem={renderSpeciesList} />
        )}
        <Pagination
          align='center'
          current={currentPage}
          total={filteredSpecies.length}
          pageSize={pageSize}
          onChange={handlePageChange}
          showSizeChanger
          showQuickJumper
          showTotal={(total) => `Total ${total} items`}
          style={{ marginTop: 20, textAlign: 'center' }}
        />
      </Card>
      {selectedApi === "sinp" ? (
        <HabitatsDirectiveSINPModal
          visible={isModalVisible}
          onOk={handleOk(setIsModalVisible)}
          onCancel={handleCancel(setIsModalVisible)}
        />
      ) : (
        <HabitatsDirectiveModal
          visible={isModalVisible}
          onOk={handleOk(setIsModalVisible)}
          onCancel={handleCancel(setIsModalVisible)}
        />
      )}
      <ZnieffModal
        visible={isZnieffModalVisible}
        onOk={handleOk(setZnieffModalVisible)}
        onCancel={handleCancel(setZnieffModalVisible)}
      />
      <IUCNRedListModal
        visible={isIUCNRedListIsModalVisible}
        onOk={handleOk(setIUCNRedListIsModalVisible)}
        onCancel={handleCancel(setIUCNRedListIsModalVisible)}
      />
      <InvasiveModal
        visible={isInvasiveModalVisible}
        onOk={handleOk(setIsInvasiveModalVisible)}
        onCancel={handleCancel(setIsInvasiveModalVisible)}
      />
      <FranceRedListModal
        visible={isFranceRedListModalVisible}
        onOk={handleOk(setFranceRedListIsModalVisible)}
        onCancel={handleCancel(setFranceRedListIsModalVisible)}
      />
      {selectedApi === "sinp" ? (
        <ProtectedSINPModal
          visible={isProtectedIsModalVisible}
          onOk={handleOk(setProtectedIsModalVisible)}
          onCancel={handleCancel(setProtectedIsModalVisible)}
        />
      ) : (
        <ProtectedListModal
          visible={isProtectedIsModalVisible}
          onOk={handleOk(setProtectedIsModalVisible)}
          onCancel={handleCancel(setProtectedIsModalVisible)}
        />
      )}
      <EuropeanRedListModal
        visible={isEuropeanRedListModal}
        onOk={handleOk(setEuropeanRedListModal)}
        onCancel={handleCancel(setEuropeanRedListModal)}
      />

    </div>
  );
};
export { SpeciesObservations, SpeciesPredictions };
